// Format helpers for metadata fields.
// Canonical storage: dates as ISO yyyy-mm-dd, numbers as raw "1234.56", text as-is.

/** Format a file size in bytes to a human-readable string (Ko / Mo / Go). */
export function formatFileSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
}

export const DATE_FORMATS = [
  { value: "dd/mm/yyyy", label: "JJ/MM/AAAA", example: "31/12/2024" },
  { value: "yyyy-mm-dd", label: "AAAA-MM-JJ (ISO)", example: "2024-12-31" },
  { value: "dd/mm/yyyy hh:mm", label: "JJ/MM/AAAA HH:MM", example: "31/12/2024 18:30" },
  { value: "mm/yyyy", label: "MM/AAAA", example: "12/2024" },
  { value: "yyyy", label: "AAAA", example: "2024" },
];

export const NUMBER_FORMATS = [
  { value: "1 234,56", label: "1 234,56 (français)", example: "1 234,56" },
  { value: "1,234.56", label: "1,234.56 (anglais)", example: "1,234.56" },
  { value: "1 234,56 €", label: "1 234,56 € (euros FR)", example: "1 234,56 €" },
  { value: "1234.56", label: "1234.56 (brut)", example: "1234.56" },
];

export const TEXT_FORMATS = [
  { value: "", label: "Libre", example: "Texte libre" },
  { value: "uppercase", label: "MAJUSCULES", example: "EXEMPLE" },
  { value: "capitalize", label: "Capitalisé", example: "Exemple De Texte" },
];

export function formatsFor(fieldType) {
  if (fieldType === "date") return DATE_FORMATS;
  if (fieldType === "number") return NUMBER_FORMATS;
  if (fieldType === "text") return TEXT_FORMATS;
  return [];
}

// ---------- DATE ----------
function pad(n) { return String(n).padStart(2, "0"); }

// Returns ISO yyyy-mm-dd (or yyyy-mm-ddThh:mm for date+time) from a display string
export function parseDateInput(display, format) {
  if (!display) return "";
  const s = String(display).trim();
  switch (format) {
    case "dd/mm/yyyy": {
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (!m) return null;
      return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
    }
    case "yyyy-mm-dd": {
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
    }
    case "dd/mm/yyyy hh:mm": {
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      return `${m[3]}-${pad(m[2])}-${pad(m[1])}T${pad(m[4])}:${m[5]}`;
    }
    case "mm/yyyy": {
      const m = s.match(/^(\d{1,2})\/(\d{4})$/);
      if (!m) return null;
      return `${m[2]}-${pad(m[1])}-01`;
    }
    case "yyyy": {
      return /^\d{4}$/.test(s) ? `${s}-01-01` : null;
    }
    default:
      return s;
  }
}

// Display ISO canonical → human format
export function displayDate(iso, format) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return iso;
  const [, y, mo, d, hh, mm] = m;
  switch (format) {
    case "yyyy-mm-dd": return `${y}-${mo}-${d}`;
    case "dd/mm/yyyy hh:mm": return `${d}/${mo}/${y} ${hh || "00"}:${mm || "00"}`;
    case "mm/yyyy": return `${mo}/${y}`;
    case "yyyy": return y;
    case "dd/mm/yyyy":
    default: return `${d}/${mo}/${y}`;
  }
}

// ---------- NUMBER ----------
// Parse user input → canonical numeric string "1234.56" or "" / null
export function parseNumberInput(display, format) {
  if (display === null || display === undefined || display === "") return "";
  let s = String(display).trim().replace(/[€$£\s\u00a0]/g, "");
  if (!s) return "";
  // Determine separator policy from format
  const fr = format === "1 234,56" || format === "1 234,56 €";
  const us = format === "1,234.56";
  if (fr) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (us) {
    s = s.replace(/,/g, "");
  }
  // Allow generic raw
  if (!/^-?\d+(\.\d+)?$/.test(s)) return null;
  return s;
}

export function displayNumber(canonical, format) {
  if (canonical === null || canonical === undefined || canonical === "") return "";
  const n = Number(canonical);
  if (Number.isNaN(n)) return String(canonical);
  const formatFR = (val) => {
    const [int, dec] = val.toFixed(2).split(".");
    const intGrouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${intGrouped},${dec}`;
  };
  const formatUS = (val) => {
    const [int, dec] = val.toFixed(2).split(".");
    return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${dec}`;
  };
  switch (format) {
    case "1,234.56": return formatUS(n);
    case "1 234,56 €": return `${formatFR(n)} €`;
    case "1234.56": return n.toString();
    case "1 234,56":
    default: return formatFR(n);
  }
}

// ---------- TEXT ----------
export function applyTextFormat(value, format) {
  if (!value) return value || "";
  switch (format) {
    case "uppercase": return value.toUpperCase();
    case "capitalize":
      return value.replace(/\b([\p{L}])([\p{L}]*)/gu, (_, a, b) => a.toUpperCase() + b.toLowerCase());
    default: return value;
  }
}

// Generic display: from canonical value to user format
export function displayValue(value, fieldType, format) {
  if (value === null || value === undefined || value === "") return "";
  if (fieldType === "date") return displayDate(value, format);
  if (fieldType === "number") return displayNumber(value, format);
  if (fieldType === "text") return applyTextFormat(String(value), format);
  return String(value);
}

// Generic parse: from user input to canonical value (returns null if invalid)
export function parseValue(input, fieldType, format) {
  if (input === null || input === undefined || input === "") return "";
  if (fieldType === "date") return parseDateInput(input, format);
  if (fieldType === "number") return parseNumberInput(input, format);
  if (fieldType === "text") return applyTextFormat(String(input), format);
  return String(input);
}

export function placeholderFor(fieldType, format) {
  const opts = formatsFor(fieldType);
  const opt = opts.find((o) => o.value === format) || opts[0];
  return opt?.example || "";
}
